<template>
  <v-container class="transparent" fluid>
    <bread-crumb :breadcrumb-data="breadcrumbData"></bread-crumb>
    <v-sheet class="pa-2" min-height="600" rounded>
      <v-row>
        <v-col class="text-left" cols="12">
          <v-btn v-show="!filtersVisible" dark elevation="0" small style="text-transform: none"
                 @click="filtersVisible = true">
            Filter
            <v-icon small>mdi-chevron-double-right</v-icon>
          </v-btn>
        </v-col>

        <v-col :cols="filtersVisible ? 2 : 0">
          <v-slide-y-transition>
            <v-sheet v-if="filtersVisible" class="pa-2" elevation="1" outlined rounded>
              <v-row no-gutters>
                <v-col class="my-3" cols="12">
                  <v-sheet class="font-weight-bold" style="font-size: 1.1rem;">
                    Filters
                    <v-btn class="float-end" dark elevation="0" small style="text-transform: none"
                           @click="filtersVisible = false">
                      Hide
                      <v-icon small>mdi-chevron-double-left</v-icon>
                    </v-btn>
                  </v-sheet>
                </v-col>

                <v-col class="my-3" cols="12">
                  <v-autocomplete
                      v-model="free_text_input"
                      :search-input="searchText"
                      :items="suggestions"
                      :loading="searching"
                      @update:search-input="handleSearchInput"
                      clearable
                      dense
                      item-text="text"
                      item-value="value"
                      label="Enter substrate or substrate:site"
                      outlined
                      @keydown.enter="applyFreeTextInput"
                  >
                    <template v-slot:item="data">
                      <div v-html="highlightMatch(data.item.text)"></div>
                    </template>
                  </v-autocomplete>


                </v-col>
                <v-col class="mb-3" cols="12">
                  <span class="font-weight-bold mr-2" style="font-size: 0.8rem">Examples:</span>
                  <span
                      v-for="(item, idx) in ['MAPK3','P27361','ENSP00000367263', 'MAPK3:Y204','P27361:Y204','ENSP00000367263:S135']"
                      :key="item"
                  >
                    <a
                        class="text-decoration-underline font-weight-medium mr-2"
                        href="javascript:void(0)"
                        style="font-size: 0.8rem"
                        @click.prevent="applyExample(item)"
                    >
                      {{ item }}
                    </a>


                  </span>
                </v-col>

                <v-col class="text-center my-3" cols="12">
                  <v-row align="center" justify="center" no-gutters>
                    <v-col cols="12" lg="6" md="6" xl="6">
                      <v-btn class="ma-2" color="primary" dense small @click="applyFreeTextInput">Apply</v-btn>
                    </v-col>
                    <v-col cols="12" lg="6" md="6" xl="6">
                      <v-btn class="ma-2" color="secondary" dense small @click="resetFilters">Reset</v-btn>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="12">
                  <v-sheet class="red--text" style="font-size: 0.8rem">
                    <v-icon color="red" small>mdi-information</v-icon>
                    Single Search accepts substrate (gene symbol, UniProt, or Ensembl protein) or substrate:site
                    queries.
                  </v-sheet>
                </v-col>
              </v-row>
            </v-sheet>
          </v-slide-y-transition>
        </v-col>

        <v-col :cols="filtersVisible ? 10 : 12">
          <v-sheet class="pa-2" elevation="2" outlined rounded>
<!--            {{result_data.phosphatase_protein}}-->
            <v-row v-if="loading" align="center" justify="center">
              <v-progress-circular color="primary" indeterminate size="70"></v-progress-circular>
            </v-row>
            <v-sheet v-if="result_data?.summary?.length">
              <!--              <h3 class="mx-3">Results:</h3>-->
              <v-row class="no-gutters">
                <v-col v-if="result_data.substrate_info" cols="12" md="6">
                  <v-sheet>
                    <v-card-title class="font-weight-bold" style="font-size: 1rem">Substrate information:</v-card-title>
                    <v-card-text>
                      <v-row v-if="result_data.substrate_info.substrate">
                        <v-col cols="3"><span class="font-weight-bold">Name:</span></v-col>
                        <v-col cols="9">{{ result_data.substrate_info.substrate }}</v-col>
                      </v-row>
                      <v-row v-if="result_data.substrate_info.ensembl_gene">
                        <v-col cols="3"><span class="font-weight-bold">Ensembl Gene:</span></v-col>
                        <v-col cols="9">
                          <a :href="'https://useast.ensembl.org/homo_sapiens/Gene/Summary?g=' + result_data.substrate_info.ensembl_gene + '&db=core'"
                             target="_blank">
                            {{ result_data.substrate_info.ensembl_gene }}
                          </a>
                        </v-col>
                      </v-row>

                      <v-row v-if="result_data.substrate_info.ensembl_protein">
                        <v-col cols="3"><span class="font-weight-bold">Ensembl Protein:</span></v-col>
                        <v-col cols="9">
                          <a :href="'https://www.ensembl.org/id/' + result_data.substrate_info.ensembl_protein[0]"
                             target="_blank">
                            {{ result_data.substrate_info.ensembl_protein[0] }}
                          </a>
                        </v-col>
                      </v-row>

                      <v-row v-if="result_data.substrate_info.uniprot">
                        <v-col cols="3"><span class="font-weight-bold">Uniprot:</span></v-col>
                        <v-col cols="9">
                          <a :href="`https://www.uniprot.org/uniprotkb/${result_data.substrate_info.uniprot}`"
                             target="_blank">
                            {{ result_data.substrate_info.uniprot }}
                          </a>
                        </v-col>
                      </v-row>

                      <v-row v-if="result_data.substrate_info.hgnc_id">
                        <v-col cols="3"><span class="font-weight-bold">HGNC ID:</span></v-col>
                        <v-col cols="9">
                          <a :href="`https://www.genenames.org/data/gene-symbol-report/#!/hgnc_id/${result_data.substrate_info.hgnc_id}`"
                             target="_blank">
                            {{ result_data.substrate_info.hgnc_id }}
                          </a>
                        </v-col>
                      </v-row>

                      <v-row v-if="result_data.substrate_info.description">
                        <v-col cols="3"><span class="font-weight-bold">Description:</span></v-col>
                        <v-col cols="9">{{ result_data.substrate_info.description }}</v-col>
                      </v-row>

                      <v-row v-if="result_data.substrate_info.gene_summary">
                        <v-col cols="3"><span class="font-weight-bold">Gene Summary:</span></v-col>
                        <v-col cols="9">
                          <div>
                            <span v-if="!showFullSummary">
                              {{ first100Words }}
                              <span v-if="hasMoreWords">... <a @click="showFullSummary = true">more</a></span>
                            </span>
                            <span v-else>
                              {{ result_data.substrate_info.gene_summary }} <a @click="showFullSummary = false">less</a>
                            </span>
                          </div>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-sheet>
                </v-col>

                <v-col v-if="result_data.protein_id" cols="12" md="6">
                  <v-sheet>
                    <v-card-title class="font-weight-bold" style="font-size: 1rem">Knowledge and Experimental Evidence
                      Plots:
                    </v-card-title>
                    <v-card-text class="pa-3">

                      <v-tabs v-model="activeTab" bg-color="grey lighten-4" color="primary" grow>

                        <v-tab style="text-transform: none">Lollipop plot</v-tab>
                        <v-tab style="text-transform: none">Score Scatter plot</v-tab>

                        <!-- kinase -->
                        <v-tab
                            v-if="result_data.kinase_protein && result_data.kinase_protein.length > 0"
                            style="text-transform: none"
                        >
                          Kinase-protein Scatter plot
                        </v-tab>

                        <!-- phosphatase -->
                        <v-tab
                            v-if="result_data.phosphatase_protein && result_data.phosphatase_protein.length > 0"
                            style="text-transform: none"
                        >
                          Phosphatase-protein Scatter plot
                        </v-tab>

                        <v-tab style="text-transform: none">Bar chart</v-tab>

                      </v-tabs>

                      <v-tabs-items v-model="activeTab">

                        <!-- 0 -->
                        <v-tab-item>
                          <v-card-text>
                            <lollipop-chart-new
                                :protein_id="result_data.protein_id"
                                :tdata="result_data.lollipop"
                            />
                          </v-card-text>
                        </v-tab-item>

                        <!-- 1 -->
                        <v-tab-item>
                          <v-card-text>
                            <ptm-score-scatter-plot
                                :show-labels="true"
                                :tdata="result_data.score"
                            />
                          </v-card-text>
                        </v-tab-item>

                        <!-- kinase -->
                        <v-tab-item
                            v-if="result_data.kinase_protein && result_data.kinase_protein.length > 0"
                        >
                          <v-card-text>
                            <meta-scatter-plot
                                :data="result_data.kinase_protein"
                                type="kinase"
                            />
                          </v-card-text>
                        </v-tab-item>

                        <!-- phosphatase -->
                        <v-tab-item
                            v-if="result_data.phosphatase_protein && result_data.phosphatase_protein.length > 0"
                        >
                          <v-card-text>
                            <meta-scatter-plot
                                :data="result_data.phosphatase_protein"
                                type="phosphatase"
                            />
                          </v-card-text>
                        </v-tab-item>

                        <!-- last -->
                        <v-tab-item>
                          <v-card-text>
                            <bar-for-single-search
                                :key="result_data.protein_id + '-bar'"
                                :tdata="result_data.summary"
                            />
                          </v-card-text>
                        </v-tab-item>

                      </v-tabs-items>

                    </v-card-text>

<!--                    <v-card-text class="pa-3">-->
<!--                      <v-tabs v-model="activeTab" bg-color="grey lighten-4" color="primary" grow>-->
<!--                        <v-tab style="text-transform: none">Lollipop plot</v-tab>-->
<!--                        <v-tab style="text-transform: none">Score Scatter plot</v-tab>-->
<!--                        <v-tab style="text-transform: none">Kinase-protein Scatter plot</v-tab>-->
<!--                        <v-tab style="text-transform: none">Phosphatase-protein Scatter plot</v-tab>-->
<!--                        <v-tab style="text-transform: none">Bar chart</v-tab>-->
<!--                      </v-tabs>-->

<!--                      <v-tabs-items v-model="activeTab">-->
<!--                        <v-tab-item>-->
<!--                          <v-card-text>-->
<!--                            <lollipop-chart-new-->
<!--                                :protein_id="result_data.protein_id"-->
<!--                                :tdata="result_data.lollipop"-->
<!--                            />-->
<!--                          </v-card-text>-->
<!--                        </v-tab-item>-->

<!--                        <v-tab-item>-->
<!--                          <v-card-text>-->
<!--                            <ptm-score-scatter-plot-->
<!--                                :show-labels="true"-->
<!--                                :tdata="result_data.score"-->
<!--                            />-->
<!--                          </v-card-text>-->
<!--                        </v-tab-item>-->
<!--                        <v-tab-item>-->
<!--                          <v-card-text>-->
<!--                            <meta-scatter-plot :data="result_data.kinase_protein" type="kinase" />-->
<!--                          </v-card-text>-->
<!--                        </v-tab-item>-->
<!--                        <v-tab-item>-->
<!--                          <v-card-text>-->
<!--                            <meta-scatter-plot :data="result_data.phosphatase_protein" type="phosphatase" />-->
<!--                          </v-card-text>-->
<!--                        </v-tab-item>-->

<!--                        <v-tab-item>-->
<!--                          <v-card-text>-->
<!--                            <bar-for-single-search-->
<!--                                :key="result_data.protein_id + '-bar'"-->
<!--                                :tdata="result_data.summary"-->
<!--                            />-->
<!--                          </v-card-text>-->
<!--                        </v-tab-item>-->
<!--                      </v-tabs-items>-->

<!--                    </v-card-text>-->

                  </v-sheet>
                </v-col>
              </v-row>
            </v-sheet>

            <v-sheet>
              <home-table v-if="!loading && result_data.summary.length > 0" :tdata="result_data.summary"
                          class="mx-3"></home-table>
            </v-sheet>
          </v-sheet>
        </v-col>
      </v-row>
    </v-sheet>
  </v-container>
</template>

<script>
import Axios from "axios";
import _ from "lodash";
import homeTable from "@/components/HomeTable.vue";
import LollipopChartNew from "@/components/LollipopChar_new.vue";
import BarForSingleSearch from "@/components/BarForSinglesearch.vue";
import Helpicon from "@/components/sub/helpicon.vue";
import BreadCrumb from "@/components/sub/breadcrumb.vue";
import PtmScoreScatterPlot from "@/components/PtmScoreScatterPlot.vue";
import metaScatterPlot from "@/components/MetaScatterPlot.vue";
import {apiprefix} from "@/main";
export default {
  name: "SingleSearch",
  components: {
    PtmScoreScatterPlot,
    BreadCrumb,
    Helpicon,
    LollipopChartNew,
    homeTable,
    BarForSingleSearch,
    metaScatterPlot
  },
  data() {
    return {
      cachedGeneSuggestions: [],
      cachedSiteSuggestions: {},
      lastQueriedGene: "",
      suggestions: [],
      filtersVisible: true,
      showFullSummary: false,
      loading: true,
      searching: false,
      activeTab: 0,
      free_text_input: "MAPK3",
      searchText: "MAPK3",
      default_result_data: {summary: [], lollipop: [], protein_id: null, substrate_info: {}, score: []},
      result_data: {summary: [], lollipop: [], protein_id: null, substrate_info: {}, score: [],kinase_protein:[],phosphatase_protein:[]},
    };
  },
  watch: {
    "$route.query.item": {
      immediate: true,
      handler(newItem) {
        if (!newItem) return;

        this.free_text_input = newItem;
        this.suggestions = [{ text: newItem, value: newItem }];

        this.fetchResult(newItem, this.$route.query.ptm_type || "phosphorylation");
      }
    },
    free_text_input(val) {
      this.searchText = val
    }
  },


  methods: {
    handleSearchInput(val) {
      this.searchText = val
      this.onSearch(val)
    },
    async onSearch(val) {
      const query = (val || "").toString().trim();

      if (!query || query.length < 2) {
        this.suggestions = [];
        this.cachedGeneSuggestions = [];
        return;
      }

      if (query.startsWith("ENSP") && query.length < 13) {
        this.suggestions = [];
        return;
      }

      this.searching = true;

      try {
        if (query.includes(":")) {
          const [gene, partialSite] = query.split(":");

          if (!gene) {
            this.suggestions = [];
            return;
          }

          const cached = this.cachedSiteSuggestions[gene] || [];

          if (gene === this.lastQueriedGene && cached.length > 0) {
            this.suggestions = cached
                .filter(site =>
                    site.toUpperCase().includes((partialSite || "").toUpperCase())
                )
                .map(site => ({
                  text: `${gene}:${site}`,
                  value: `${gene}:${site}`
                }));
          } else {
            const res = await Axios.get(`${apiprefix}/api/site_suggestions?gene=${gene}`);
            const sites = res.data.sites || [];

            this.cachedSiteSuggestions[gene] = sites;
            this.lastQueriedGene = gene;

            this.suggestions = sites
                .filter(site =>
                    site.toUpperCase().includes((partialSite || "").toUpperCase())
                )
                .map(site => ({
                  text: `${gene}:${site}`,
                  value: `${gene}:${site}`
                }));
          }

        } else {
          const res = await Axios.get(`${apiprefix}/api/gene_suggestions?query=${query}`);
          const genes = res.data.genes || [];

          this.cachedGeneSuggestions = genes;

          this.suggestions = genes.map(gene => ({
            text: gene,
            value: gene
          }));
        }

      } catch (err) {
        console.error("onSearch error:", err);
        this.suggestions = [];
      } finally {
        this.searching = false;
      }
    },

    immediateSearch(val) {
      const query = (val || "").trim();
      if (!query || query.length < 2) {
        this.suggestions = [];
        return Promise.resolve();
      }
      if (query.startsWith("ENSP") && query.length < 13) return Promise.resolve();

      if (query.includes(":")) {
        const [gene, partialSite] = query.split(":");
        return Axios.get(`${apiprefix}/api/site_suggestions?gene=${gene}`)
            .then((res) => {
              const sites = res.data.sites || [];
              this.suggestions = sites
                  .filter((s) => s.toUpperCase().includes((partialSite || "").toUpperCase()) )
                  .map((s) => ({text: `${gene}:${s}`, value: `${gene}:${s}`}));
            })
            .catch(() => {
              this.suggestions = [];
            });
      } else {
        return Axios.get(`${apiprefix}/api/gene_suggestions?query=${query}`)
            .then((res) => {
              const genes = res.data.genes || [];
              this.suggestions = genes.map((g) => ({text: g, value: g}));
            })
            .catch(() => {
              this.suggestions = [];
            });
      }
    },

    async applyFreeTextInput() {
      let selected = (this.free_text_input || "").trim();
      if (!selected) return;

      if (this.suggestions.length === 0) {
        await this.immediateSearch(selected);
        await this.$nextTick();
      }
      if (this.suggestions.length > 0) {
        const inSuggestions = this.suggestions.some(
            s => s.value.toUpperCase() === selected.toUpperCase()
        );
        if (!inSuggestions) {
          const first = this.suggestions[0];
          selected = first.value || first.text;
          this.free_text_input = selected;
        }
      }

      const cleaned = selected.replace("：", ":").trim();
      const [substrate, site] = cleaned.split(":");
      const id = site ? `${substrate.trim()}:${site.trim()}` : substrate.trim();


      this.fetchResult(id);
    },
    async applyExample(item) {
      if (!item) return;
      this.free_text_input = item;
      this.suggestions = [{ text: item, value: item }];

      await this.$nextTick();
      this.applyFreeTextInput();
    },

    highlightMatch(text) {
      if (!text) return "";
      const safeSearch = (this.free_text_input || "").toString();
      if (!safeSearch) return text;
      const parts = safeSearch.split(":");
      const input = parts[1] || parts[0];
      if (!input) return text;
      const regex = new RegExp(`(${_.escapeRegExp(input)})`, "gi");
      return text.replace(regex, "<b>$1</b>");
    },

    resetFilters() {
      this.$router.replace({
        query: { item: "MAPK3", ptm_type: "phosphorylation" }
      })
    },
    fetchResult(id, ptm_type = 'phosphorylation') {
      this.loading = true;
      Axios.post(`${apiprefix}/api/singlesearch/`, {}, {
        params: {
          id: id.trim().toUpperCase(),
          ptm_type: ptm_type.trim()
        }
      })
          .then(res => {
            if (res.data.status) {
              const summary = res.data.data.summary || [];
              const hasSite = id.includes(":");
              if (hasSite && summary.length === 1) {
                this.loading = false;
                const internal_id = summary[0].internal_id;
                const ptm_encoded = encodeURIComponent(ptm_type.trim());
                const targetPath = `/single-search/${internal_id}?ptm_type=${ptm_encoded}`;
                if (this.$route.fullPath !== targetPath) {
                  this.$router.push(targetPath).catch(err => {
                    if (err.name !== 'NavigationDuplicated') console.error(err);
                  });
                }
                return;
              }
              this.result_data = {...res.data.data};
              if (id === "MAPK3") {
                sessionStorage.setItem("default_result_data", JSON.stringify(res.data.data));
                this.default_result_data = {...res.data.data};
              }
            }
            this.loading = false;
          })
          .catch(err => {
            console.error(err);
            this.loading = false;
          });
    }

  },

  computed: {
    wordsArray() {
      return this.result_data?.substrate_info?.gene_summary?.split(" ") || [];
    },
    first100Words() {
      return this.wordsArray.slice(0, 100).join(" ");
    },
    hasMoreWords() {
      return this.wordsArray.length > 100;
    },
    breadcrumbData() {
      return [{text: "Home", disabled: false, to: "/"}, {text: "Single Search", disabled: true}];
    },
  },
  mounted() {
    if (!this.$route.query.item) {
      this.free_text_input = "MAPK3"
      this.searchText = "MAPK3"
      this.fetchResult("MAPK3")
    }
  }
};
</script>

<style>
</style>
